//@ts-check
/**
 * @class CMenu
 * @extends {HTMLElement}
 *
 * This custom element manages the navigation menu, including multiple levels of panels.
 */
class CMenu extends HTMLElement {
  constructor() {
    super()

    // Main elements
    this.containerHeader = document.querySelector('.container-header')
    this.panel = this.querySelector('[data-panel-menu]')
    this.menuLevel1 = this.querySelector('.menu-level-1')
    this.menuBottom = this.querySelector('.menu-bottom')
    this.menuBurger = this.querySelector('.menu-burger')

    // Buttons
    this.btnsToggleMenu = [...this.querySelectorAll('[data-btn-toggle-menu]')]
    this.btnTrapFocus = this.querySelector('[data-trap-focus]')

    // Level 2
    this.moveToLevel2Buttons = [
      ...this.querySelectorAll('[data-move-to-level-2]'),
    ]
    this.level2Panels = [...this.querySelectorAll('[data-panel-level-2]')]

    // Level 3
    this.moveToLevel3Buttons = [
      ...this.querySelectorAll('[data-move-to-level-3]'),
    ]
    this.level3Panels = [...this.querySelectorAll('[data-panel-level-3]')]

    // Sticky product button on mobile
    this.btnProductStickyMobile = document.querySelector(
      '.product-btn-sticky.v-mobile'
    )?.parentElement

    // Event handlers storage
    this.boundToggleMenu = this.handleToggleMenu.bind(this)
    this.level2Handlers = new Map()
    this.level3Handlers = new Map()
  }

  connectedCallback() {
    this.btnsToggleMenu.forEach((btn) => {
      btn.addEventListener('click', this.boundToggleMenu)
    })

    this.moveToLevel2Buttons.forEach((btn) => {
      const handler = () => this.handleLevelChange(2, btn)
      this.level2Handlers.set(btn, handler)
      btn.addEventListener('click', handler)
    })

    this.moveToLevel3Buttons.forEach((btn) => {
      const handler = () => this.handleLevelChange(3, btn)
      this.level3Handlers.set(btn, handler)
      btn.addEventListener('click', handler)
    })
  }

  disconnectedCallback() {
    this.btnsToggleMenu.forEach((btn) => {
      btn.removeEventListener('click', this.boundToggleMenu)
    })

    this.moveToLevel2Buttons.forEach((btn) => {
      const handler = this.level2Handlers.get(btn)
      if (handler) {
        btn.removeEventListener('click', handler)
      }
    })
    this.level2Handlers.clear()

    this.moveToLevel3Buttons.forEach((btn) => {
      const handler = this.level3Handlers.get(btn)
      if (handler) {
        btn.removeEventListener('click', handler)
      }
    })
    this.level3Handlers.clear()
  }

  /**
   * Toggles the visibility of an element and its 'is-open' class.
   * @param {Element | null} element - The element to toggle.
   * @param {boolean} [force] - Force a state. True to show, false to hide.
   */
  toggleElement(element, force) {
    if (!element) return
    const isOpen = element.classList.toggle('is-open', force)
    element.toggleAttribute('hidden', !isOpen)
  }

  /**
   * Handles toggling the main menu (open/close).
   * @param {Event} event - The click event.
   */
  handleToggleMenu(event) {
    const isCurrentlyOpen = this.containerHeader?.classList.contains('is-open')
    const openState = !isCurrentlyOpen

    if (!openState) {
      this.closeAllPanels()
      this.menuBurger?.focus()
    } else {
      this.btnTrapFocus?.focus()
    }

    event.currentTarget.setAttribute('aria-expanded', String(openState))

    this.toggleElement(this.panel, openState)
    this.containerHeader?.classList.toggle('is-open', openState)
    document.documentElement.classList.toggle('is-overflow-hidden', openState)
    this.btnProductStickyMobile?.classList.toggle('is-hide', openState)
  }

  /**
   * Handles navigation between menu levels.
   * @param {number} level - The menu level (2 or 3).
   * @param {HTMLElement} btn - The button that was clicked.
   */
  handleLevelChange(level, btn) {
    const panelAttribute = `data-panel-level-${level}`
    const buttonAttribute = `data-move-to-level-${level}`
    const targetPanelId = btn.getAttribute(buttonAttribute)
    const targetPanel = this.querySelector(
      `[${panelAttribute}='${targetPanelId}']`
    )
    if (!targetPanel) return

    const isGoBack = btn.classList.contains('go-back')
    const isOpening = isGoBack
      ? false
      : !targetPanel.classList.contains('is-open')

    // Close any nested panels if we are closing the current one.
    if (!isOpening && level === 2) {
      this.closeNestedPanels(targetPanel)
    }

    // Close all other panels at the same level.
    const allPanels = level === 2 ? this.level2Panels : this.level3Panels
    allPanels.forEach((p) => {
      if (p !== targetPanel) this.toggleElement(p, false)
    })

    // And deactivate their corresponding buttons
    const allButtons =
      level === 2 ? this.moveToLevel2Buttons : this.moveToLevel3Buttons
    allButtons.forEach((b) => {
      if (
        b.getAttribute(buttonAttribute) !== targetPanelId &&
        !b.classList.contains('go-back')
      ) {
        b.classList.remove('is-open')
      }
    })

    // Toggle the target panel and its opener button
    this.toggleElement(targetPanel, isOpening)
    const openerButton = this.querySelector(
      `[${buttonAttribute}='${targetPanelId}']:not(.go-back)`
    )
    openerButton?.classList.toggle('is-open', isOpening)

    // Update main container classes
    this.updateParentContainerClasses(level, btn, isOpening)
  }
  /**
   * Updates the classes of parent containers based on the current menu state.
   * @param {number} level - The current menu level.
   * @param {HTMLElement} btn - The button that was clicked.
   * @param {boolean} isOpening - Whether a panel is being opened.
   */
  updateParentContainerClasses(level, btn, isOpening) {
    const anyPanelOpenAtLevel2 = this.level2Panels.some((p) =>
      p.classList.contains('is-open')
    )
    this.menuLevel1?.classList.toggle('is-level2-open', anyPanelOpenAtLevel2)
    this.menuBottom?.classList.toggle('is-level2-open', anyPanelOpenAtLevel2)

    if (level === 2 && !isOpening) {
      // Ensure L3 class is removed if we close L2
      this.menuLevel1?.classList.remove('is-level3-open')
    }

    if (level === 3) {
      const anyPanelOpenAtLevel3 = this.level3Panels.some((p) =>
        p.classList.contains('is-open')
      )
      const parentPanel = btn.closest('[data-panel-level-2]')
      parentPanel?.classList.toggle('is-remove-scroll', anyPanelOpenAtLevel3)
      this.menuLevel1?.classList.toggle('is-level3-open', anyPanelOpenAtLevel3)
    }
  }

  /**
   * Closes all menu panels and resets their states.
   */
  closeAllPanels() {
    this.level2Panels.forEach((p) => {
      this.toggleElement(p, false)
      this.closeNestedPanels(p)
    })
    this.moveToLevel2Buttons.forEach((b) => b.classList.remove('is-open'))

    this.menuLevel1?.classList.remove('is-level2-open', 'is-level3-open')
    this.menuBottom?.classList.remove('is-level2-open')
  }

  /**
   * Closes level 3 panels within a given level 2 panel.
   * @param {Element} level2Panel
   */
  closeNestedPanels(level2Panel) {
    const level3Panels = [
      ...level2Panel.querySelectorAll('[data-panel-level-3]'),
    ]
    level3Panels.forEach((p) => this.toggleElement(p, false))

    const level3Buttons = [
      ...level2Panel.querySelectorAll('[data-move-to-level-3]'),
    ]
    level3Buttons.forEach((b) => b.classList.remove('is-open'))
  }
}

if (!customElements.get('c-menu')) {
  customElements.define('c-menu', CMenu)
}
